import { DocumentNode, gql, useMutation } from '@apollo/client';
import { MenuItem, useDisclosure, useToast } from '@chakra-ui/react';
import * as React from 'react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { logError } from '@/imports/logging/ClientLogger';
import { PinIcon } from '@/imports/ui/chakra/customIcon';

import { PinPostDropdownItem_PostFragment } from './graphql-types/PinPostDropdownItem_PostFragment';
import {
  PinPostDropdownItem_UpdatePostV2,
  PinPostDropdownItem_UpdatePostV2Variables,
} from './graphql-types/PinPostDropdownItem_UpdatePostV2';

const POST_FRAGMENT = gql`
  fragment PinPostDropdownItem_PostFragment on PostV2 {
    _id
    pinned
    group {
      _id
      pinnedPostV2 {
        _id
      }
    }
  }
`;

const EDIT_POST_PINNED_STATE = gql`
  mutation PinPostDropdownItem_UpdatePostV2($input: UpdatePostInput_V2!) {
    updatePostV2(input: $input) {
      data {
        _id
        ...PinPostDropdownItem_PostFragment
      }
    }
  }
  ${POST_FRAGMENT}
`;

export const PinPostDropdownItem: React.FC<{
  post: PinPostDropdownItem_PostFragment;
}> & {
  postFragment: DocumentNode;
} = ({ post }) => {
  const confirmDialog = useDisclosure();

  const [updatePost] = useMutation<
    PinPostDropdownItem_UpdatePostV2,
    PinPostDropdownItem_UpdatePostV2Variables
  >(EDIT_POST_PINNED_STATE, {
    refetchQueries: ['PinnedPost_GetPostsV2', 'postFeedV2'],
    awaitRefetchQueries: true,
  });

  const toast = useToast();

  const onConfirm = async (): Promise<void> => {
    try {
      if (post.pinned) {
        await updatePost({
          variables: {
            input: {
              id: post._id,
              data: {
                pinned: false,
              },
            },
          },
        });
      } else {
        await updatePost({
          variables: {
            input: {
              id: post._id,
              data: {
                pinned: true,
              },
            },
          },
        });
      }
      toast({
        title: `Successfully ${
          post.pinned
            ? 'unpinned'
            : post.group?.pinnedPostV2?._id
            ? 'replaced pinned'
            : 'pinned'
        } post.`,
        status: 'success',
      });
    } catch (err) {
      toast({
        title: `Failed to ${
          post.pinned ? 'unpin' : 'pin'
        } post. You can only have a single pinned post in a group at a time.`,
        status: 'error',
      });
      logError(err);
    }
  };

  return (
    <>
      <MenuItem
        icon={<PinIcon strokeWidth="semibold" />}
        onClick={confirmDialog.onOpen}
      >
        {post.pinned ? 'Unpin' : 'Pin'}
      </MenuItem>
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={confirmDialog.onClose}
        title={
          post.pinned
            ? 'Unpin this Post'
            : post.group?.pinnedPostV2?._id
            ? `There's a pinned post on this group already`
            : 'Pin this Post'
        }
        body={
          post.pinned
            ? 'This will unpin the post from the top of the group, are you sure you want to do this?'
            : post.group?.pinnedPostV2?._id
            ? 'Would you like to replace the old pinned post with this one?'
            : 'This will pin the post to the top of the group.'
        }
        confirmButtonText={
          post.pinned
            ? 'Unpin Post'
            : post.group?.pinnedPostV2?._id
            ? 'Replace Pinned Post'
            : 'Pin Post'
        }
        onConfirm={onConfirm}
      />
    </>
  );
};

PinPostDropdownItem.postFragment = POST_FRAGMENT;
